/**
 * Fotos vor dem Hochladen verkleinern — ohne die Angaben zu verlieren.
 *
 * Der Server kann das nicht: ohne ffmpeg und ohne verlässliche GD-Ausstattung
 * bleibt ihm nur, entgegenzunehmen, was kommt. Also rechnet der Browser --
 * und das ist ohnehin die bessere Stelle, weil dann auch die Leitung
 * unterwegs nicht das volle Bild tragen muss.
 *
 * Ein iPhone-Foto von 3 MB wird so zu 300 bis 600 kB. Bei 40 GB freiem
 * Speicher ist das der Unterschied zwischen 13.000 und 90.000 Bildern.
 *
 * **Die EXIF-Daten bleiben erhalten**, allen voran der Aufnahmeort: sie
 * werden aus dem Original übernommen und in das verkleinerte Bild gesetzt
 * (siehe `exif.ts`). Bei einem Reisebericht ist der Ort die Angabe, nach der
 * man Jahre später sucht.
 *
 * Ein Foto, das ohnehin klein genug ist, wird **gar nicht angefasst** und
 * unverändert hochgeladen. Das erhält nicht nur alle Angaben, sondern auch
 * die volle Bildqualität — neu zeichnen kostet immer etwas.
 */

import { baueExif, findExif, isJpeg, readAufnahme, uprightExif, withExif, ausrichtung } from './exif'
import type { Aufnahme } from './exif'
import type { Uhr } from './ortszeit'
import type { Settings } from '../api/types'
import { WORTLAUT } from '../texte'

/**
 * Lange Kante und Qualität -- die Vorgaben, wenn der Server nichts sagt.
 *
 * Sie stehen hier als Rückfalllinie, nicht als Gesetz: die geltenden Werte
 * kommen aus den Einstellungen (`/api/settings`) und lassen sich im Betrieb
 * ändern, ohne die Oberfläche neu zu bauen. Nur solange diese Abfrage noch
 * unterwegs ist -- oder scheitert -- gelten diese zwei Zahlen.
 */
export const MAX_EDGE = 2560
export const QUALITY = 80

/** Bis hierher lohnt das Verkleinern nicht. */
const KEEP_ORIGINAL_BYTES = 4 * 1024 * 1024

/**
 * Ein GIF? Gelesen an den ersten Bytes (`GIF87a`/`GIF89a`), nicht am Namen
 * und nicht am Typ, den der Browser meldet -- beides kann jemand setzen, die
 * Datei nicht.
 */
function istGif(bytes: Uint8Array): boolean {
  return (
    bytes.length > 6
    && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46
    && bytes[3] === 0x38 && (bytes[4] === 0x37 || bytes[4] === 0x39) && bytes[5] === 0x61
  )
}

export interface Prepared {
  blob: Blob
  width: number
  height: number
  /**
   * Wo und wann das Foto entstanden ist -- aus dem **Original** gelesen.
   *
   * Der Server bekommt es als Kopfzeile mitgeschickt und legt es an der Datei
   * ab. Er selbst kann es nicht lesen: EXIF auseinanderzunehmen ist Arbeit,
   * die auf einem geteilten Paket nichts zu suchen hat, und der Browser hat
   * die Datei ohnehin schon offen.
   */
  aufnahme: Aufnahme
  /**
   * Das Vorschaubild für die Kachel im Chat (#57) — oder `null`.
   *
   * **Warum es hier entsteht und nicht auf dem Server.** Auf dem Zielpaket
   * gibt es weder GD noch Imagick; hier liegt das Bild ohnehin schon auf einer
   * Leinwand, und ein zweiter Durchgang kostet Millisekunden.
   *
   * `null` bei einem GIF: der Weg unten schreibt ein JPEG, und aus einem
   * Zwinkern würde damit ein Standbild. Ein GIF ist ohnehin klein.
   */
  thumb: Blob | null
}

/**
 * Wie groß ein Vorschaubild wird, wenn die Einstellungen noch nicht da sind
 * — **800 Punkte auf der langen Kante** (1.4.0, vorher 400).
 *
 * Aus dem Betrieb, am iPad: „ziemlich grieselig". Die Kachel dort ist bis zu
 * 320 Punkte breit, bei doppelter Dichte 640 echte Bildpunkte; 400 wurde auf
 * das Doppelte gestreckt. Die Zahl steht seitdem in den Einstellungen
 * (`thumb_max_edge`) und kommt über `bildgrenzen()` hierher; dies ist nur
 * die Vorgabe für den Augenblick davor. Die Rechnung von damals darunter:
 *
 * Die Kachel im Chat ist rund 120 Punkte breit; auf einem Bildschirm mit
 * dreifacher Dichte sind das 360 echte Bildpunkte. 400 deckt das ab und ergibt
 * rund 30 Kilobyte — gegenüber einem halben bis anderthalb Megabyte, die die
 * Kachel bis #57 geladen hat.
 */
const THUMB_EDGE = 800

/**
 * Und mit welcher Güte. **Fünfundsiebzig statt der eingestellten Zahl:** ein
 * Bild, das auf einen Bruchteil seiner Kantenlänge geht, verträgt mehr
 * Kompression als eines, das jemand groß ansieht (bis 1.4.0 siebzig).
 */
const THUMB_QUALITY = 0.75

/** Die eingestellte Bildgröße; `quality` in Prozent, wie sie der Server führt. */
export interface ImageLimits {
  // Freiwillig: solange die Einstellungen noch unterwegs sind, gelten die
  // Vorgaben oben. `prepareImage()` setzt sie ohnehin selbst ein.
  maxEdge?: number
  quality?: number
  /** Die lange Kante des Vorschaubilds (1.4.0), aus `thumb_max_edge`. */
  thumbEdge?: number
  /**
   * Die Uhr der Reise — für Bilder, deren EXIF-Block keine Zone trägt.
   *
   * `DateTimeOriginal` ist Ortszeit ohne Zone. Ohne diese Angabe läse der
   * Browser sie in **seiner** Uhr, und in der Nachbearbeitung zu Hause landet
   * ein Foto damit Stunden neben seinem Platz. Die Reise weiß dagegen, wo die
   * Kamera stand.
   */
  reiseVersatz?: Uhr
}

/**
 * Welche Bildgrenzen für wen gelten (#59).
 *
 * **Eine Stelle, und nur eine.** Seit Mitlesende Fotos schicken dürfen, gibt
 * es zwei Paare in den Einstellungen — und die Frage „welches gilt hier?"
 * gehört genau einmal beantwortet. Stünde sie an jedem Aufrufer, liefe sie
 * beim dritten auseinander; das ist in diesem Projekt dreimal passiert.
 *
 * **Ohne Antwort des Servers gilt nichts von beidem**, sondern die Vorgabe in
 * `prepareImage()`. Ein Foto, das mit geratenen Grenzen verkleinert wurde,
 * sieht aus wie eines, das der Einstellung folgt.
 *
 * **Sechs Wege rufen sie** (1.1.1): der Composer nach der Rolle, die der
 * Server für die Reise nennt; Symbolbild, Chat-Hintergrund, Foto am Punkt und
 * Foto zu Punkt mit `true`, denn dort kommt nur der Autor hin. Vier davon
 * griffen das Paar vorher selbst ab — richtig, aber die Regel stand fünfmal.
 */
export function bildgrenzen(
  werte: Settings | null,
  istReisender: boolean,
): { maxEdge: number; quality: number; thumbEdge: number } | Record<string, never> {
  if (werte === null) return {}
  // The thumbnail's edge is one for both roles (1.4.0): a tile is a tile.
  const thumbEdge = werte.thumb_max_edge
  return istReisender
    ? { maxEdge: werte.image_max_edge, quality: werte.image_quality, thumbEdge }
    : { maxEdge: werte.reader_image_max_edge, quality: werte.reader_image_quality, thumbEdge }
}

export async function prepareImage(file: File, limits?: ImageLimits): Promise<Prepared> {
  const maxEdge = limits?.maxEdge ?? MAX_EDGE
  const quality = (limits?.quality ?? QUALITY) / 100

  const original = new Uint8Array(await file.arrayBuffer())
  // Vor allem anderen: das Verkleinern rechnet die Bildpunkte neu, und der
  // Block wird nur nachträglich wieder eingesetzt. Aus dem Original gelesen,
  // ist die Angabe von dem, was der Fotoapparat geschrieben hat -- nicht von
  // dem, was danach noch übrig war.
  const aufnahme = readAufnahme(original, limits?.reiseVersatz ?? null)
  const bitmap = await toBitmap(file)
  const longest = Math.max(bitmap.width, bitmap.height)

  /**
   * **Ein GIF geht unverändert durch — sonst wäre es keins mehr.**
   *
   * Der Weg unten zeichnet das Bild in ein Canvas und schreibt ein JPEG
   * daraus. Bei einem animierten GIF überlebt davon genau ein Bild: das
   * erste. Aus einem Zwinkern wird ein Standbild, und niemand sieht dem
   * Ergebnis an, woran es lag.
   *
   * Verkleinern ließe sich ein GIF nur, indem man jedes Einzelbild rechnet
   * und die Datei neu zusammensetzt -- ein eigener Kodierer im Browser.
   * Stattdessen gilt für GIFs die rohe Dateigröße: der Composer sagt vorher,
   * wenn sie zu groß ist.
   */
  if (istGif(original)) {
    const masse = { width: bitmap.width, height: bitmap.height }
    bitmap.close?.()
    return { blob: new Blob([original], { type: 'image/gif' }), ...masse, aufnahme, thumb: null }
  }

  /*
   * **Das Vorschaubild wird hier gerechnet und nicht in einem der Zweige
   * unten.** Die Bitmap wird in jedem Zweig geschlossen; wer sie danach noch
   * einmal braucht, müsste die Datei ein zweites Mal dekodieren. Einmal
   * zeichnen für alle Wege — auch für den, der das Original unverändert
   * weiterreicht: gerade dort ist es am nötigsten, denn dann ist die Kachel
   * das volle Bild.
   */
  /*
   * **Ein kleines JPEG bekommt keines** (Codereview D7).
   *
   * `vorschau()` skaliert nie hoch, rechnet aber immer -- und legte damit für
   * ein Foto, das ohnehin schon unter der Vorschaukante liegt, eine **zweite,
   * gleich große Datei** an. Sie zählt gegen das Kontingent, geht über die
   * Leitung und spart nichts.
   *
   * Die Bedingung ist dieselbe wie unten beim unveränderten Weg, um eine
   * Kante erweitert: schon JPEG **und** kleiner als die Vorschaukante. Dann
   * ist das Original seine eigene Vorschau, `thumb_url` bleibt leer, und die
   * Kachel zeigt es wie vor #57.
   */
  const thumbEdge = limits?.thumbEdge ?? THUMB_EDGE
  const thumb = isJpeg(original) && longest <= thumbEdge
    ? null
    : await vorschau(bitmap, thumbEdge)

  /*
   * Klein genug und schon JPEG: unverändert weiterreichen.
   *
   * **Aber nur aufrecht** (Codereview D3). Ein Foto mit einer Drehmarke geht
   * hier sonst mit ihr hinaus, und der Server liest die Maße aus dem
   * Dateikopf -- der steht bei einer Hochkantaufnahme quer. Drei Folgen, alle
   * beobachtbar: die Galerie reserviert das falsche Seitenverhältnis, das
   * Vorschaubild daneben ist aufrecht gezeichnet und passt nicht dazu, und im
   * PDF liegt das Foto auf der Seite, weil `PdfBook::jpegFor()` keine Marke
   * kennt.
   *
   * Neuzeichnen löst alle drei auf einmal: `createImageBitmap` richtet nach
   * der Marke aus, der Weg unten schreibt das aufrechte Bild, und
   * `keepExif()` setzt die Marke dabei auf 1. Es kostet einen Durchgang für
   * genau die Fotos, die ihn brauchen.
   */
  if (
    isJpeg(original)
    && longest <= maxEdge
    && original.byteLength <= KEEP_ORIGINAL_BYTES
    && ausrichtung(original) === 1
  ) {
    const masse = { width: bitmap.width, height: bitmap.height }
    bitmap.close?.()
    return { blob: new Blob([original], { type: 'image/jpeg' }), ...masse, aufnahme, thumb }
  }

  const scale = Math.min(1, maxEdge / longest)
  const width = Math.max(1, Math.round(bitmap.width * scale))
  const height = Math.max(1, Math.round(bitmap.height * scale))

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) throw new Error(WORTLAUT.technik.fehler.bildNichtVerkleinern)
  aufWeiss(context, width, height)
  context.drawImage(bitmap, 0, 0, width, height)
  bitmap.close?.()

  const gezeichnet = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', quality),
  )
  if (!gezeichnet) throw new Error(WORTLAUT.technik.fehler.bildNichtUmwandeln)

  return { blob: await keepExif(gezeichnet, original, aufnahme), width, height, aufnahme, thumb }
}

/**
 * Ein Vorschaubild aus einer schon dekodierten Bitmap.
 *
 * **Ohne EXIF, und das mit Absicht.** Ort und Zeit stehen am Original; sie ein
 * zweites Mal mitzuschicken hieße, den Aufnahmeort an einer Datei abzulegen,
 * die niemand herunterlädt — und wäre eine zweite Stelle, die stimmen muss.
 *
 * **Und nie fatal.** Scheitert es, bleibt es beim Original in der Kachel:
 * langsamer, aber vollständig. Ein Foto, das gar nicht durchgeht, weil sein
 * Bildchen nicht gerechnet werden konnte, wäre der schlechtere Handel.
 */
/**
 * A thumbnail from a finished JPEG -- for a picture that was not a file to
 * begin with, the drawn map of the day card (1.3.0). Same size and quality
 * as for a photo, same "never fatal".
 */
export async function vorschauAus(blob: Blob, kante: number = THUMB_EDGE): Promise<Blob | null> {
  try {
    const bitmap = await createImageBitmap(blob)
    try {
      return await vorschau(bitmap, kante)
    } finally {
      bitmap.close?.()
    }
  } catch {
    return null
  }
}

/**
 * White under the picture, before it is drawn (1.78.0) -- for both the photo
 * and its thumbnail, so that the two cannot differ.
 *
 * Everything here ends as a JPEG, and a JPEG knows no transparency: a PNG or
 * WebP with a transparent part came out **black** there, while the preview in
 * the composer (the original file) still showed it light. White is what a
 * transparent picture looks like on a page, and a JPEG from the camera has
 * nothing transparent to show it through.
 */
function aufWeiss(context: CanvasRenderingContext2D, width: number, height: number): void {
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, width, height)
}

async function vorschau(bitmap: ImageBitmap, kante: number): Promise<Blob | null> {
  try {
    const longest = Math.max(bitmap.width, bitmap.height)
    const scale = Math.min(1, kante / longest)
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const context = canvas.getContext('2d')
    if (!context) return null
    aufWeiss(context, width, height)
    context.drawImage(bitmap, 0, 0, width, height)

    return await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', THUMB_QUALITY),
    )
  } catch {
    return null
  }
}

/**
 * Aufnahmeort und -zeit aus einem fertigen Blob.
 *
 * Für den Ausgangskorb: dort liegt das verkleinerte Bild, und der EXIF-Block
 * steckt noch darin (siehe `keepExif`). Ohne diesen Weg verlöre ein offline
 * geschriebener Beitrag genau die Angaben, um die es geht — und offline
 * geschrieben wird auf einer Reise oft.
 */
export async function aufnahmeAus(
  blob: Blob,
  reiseVersatz: Uhr = null,
): Promise<Aufnahme> {
  try {
    return readAufnahme(new Uint8Array(await blob.arrayBuffer()), reiseVersatz)
  } catch {
    return { at: null, lat: null, lon: null, tz_offset_min: null, befund: 'unlesbar' }
  }
}

/**
 * Die Angaben als Kopfzeilen für den Upload.
 *
 * Als Kopfzeilen und nicht im Rumpf, weil der Rumpf die Bilddatei ist. Was
 * fehlt, wird weggelassen — eine leere Kopfzeile wäre eine Behauptung über
 * nichts, und der Server müsste sie wieder auseinandernehmen.
 */
export function aufnahmeKopf(aufnahme: Aufnahme | undefined): Record<string, string> {
  const kopf: Record<string, string> = {}
  if (!aufnahme) return kopf
  if (aufnahme.at !== null) kopf['x-aufnahme-zeit'] = aufnahme.at
  // The zone (1.87.0): what the file says, with its offset -- or, without
  // one, the camera's wall clock, which the server reads in the trip's zone
  // course. The server decides, so the outbox (which never knew the trip's
  // clock) uploads the same as the form.
  if (aufnahme.tz_offset_min !== null) {
    kopf['x-aufnahme-versatz'] = String(aufnahme.tz_offset_min)
    kopf['x-aufnahme-zone'] = 'aufnahme'
  } else if ((aufnahme.wanduhr ?? null) !== null) {
    kopf['x-aufnahme-wanduhr'] = aufnahme.wanduhr!
  }
  if (aufnahme.lat !== null && aufnahme.lon !== null) {
    kopf['x-aufnahme-ort'] = `${aufnahme.lat},${aufnahme.lon}`
  }
  return kopf
}

/**
 * Ob die Datei sich sofort als Vorschau zeigen lässt.
 *
 * Für die Vorschau vor dem Verkleinern: eine HEIC-Datei vom iPhone zeigt nur
 * Safari an, in Chrome und Firefox bliebe die Kachel schwarz. Dort wird auf
 * das umgewandelte Bild gewartet -- ein paar Zehntelsekunden später, dafür
 * ohne Loch.
 */
export function canPreviewDirectly(file: File): boolean {
  return /^image\/(jpeg|png|webp|gif)$/.test(file.type)
}

/**
 * Nimmt den EXIF-Block des Originals in das neue Bild mit.
 *
 * Geht nur, wenn das Original ein JPEG war. Eine HEIC-Datei vom iPhone trägt
 * ihre Angaben in einem anderen Behälter, an den der Browser nicht
 * heranlässt; dort bleibt es beim reinen Bild. Auf dem iPhone gibt die
 * Dateiauswahl allerdings meist ohnehin ein umgewandeltes JPEG heraus — und
 * das trägt die Angaben mit.
 */
async function keepExif(
  gezeichnet: Blob,
  original: Uint8Array,
  aufnahme: Aufnahme,
): Promise<Blob> {
  // **Aus einem JPEG wird übernommen, aus allem anderen geschrieben.**
  //
  // Übernehmen ist das Bessere, wo es geht: der Block trägt dann auch Kamera,
  // Belichtung und alles Übrige. Aus einem DNG geht es nicht -- dessen
  // Verzeichnisse verweisen auf Bilddaten von vielen Megabyte, und ein
  // APP1-Segment fasst 64 kB. Dann entsteht ein kleiner neuer aus dem, was
  // gelesen wurde.
  const vorhanden = findExif(original)
  const block = vorhanden ? uprightExif(vorhanden) : baueExif(aufnahme)
  if (!block) return gezeichnet

  try {
    const bytes = new Uint8Array(await gezeichnet.arrayBuffer())
    return new Blob([withExif(bytes, block)], { type: 'image/jpeg' })
  } catch {
    // Lieber ein Foto ohne Angaben als gar keines.
    return gezeichnet
  }
}

/**
 * Bildpunkte aus der Datei holen.
 *
 * `imageOrientation: 'from-image'` richtet das Bild nach der EXIF-Marke aus --
 * sonst läge ein Hochkant-Foto vom Telefon quer. Deshalb wird die Marke beim
 * Übernehmen auf „aufrecht" gesetzt: gedreht wurde schon.
 */
async function toBitmap(file: File): Promise<ImageBitmap> {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' })
    } catch {
      // Weiter mit dem Umweg unten.
    }
  }

  const url = URL.createObjectURL(file)
  try {
    const image = new Image()
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve()
      image.onerror = () => reject(new Error(WORTLAUT.technik.fehler.bildNichtOeffnen))
      image.src = url
    })
    return await createImageBitmap(image)
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Ob die Datei überhaupt ein Bild ist -- Videos kommen später. */
export function isImage(file: File): boolean {
  return (
    file.type.startsWith('image/') ||
    // Ohne `type` -- das passiert über die Dateien-App -- entscheidet der Name.
    // `dng` gehört dazu, seit eine ProRAW-Aufnahme im Betrieb aufschlug.
    /\.(hei[cf]|jpe?g|png|webp|gif|dng|tiff?)$/i.test(file.name)
  )
}
